/**
 * Pure undo/redo stack for the schedule editor.
 *
 * The editor keeps every version of the assignments array in a stack with a
 * cursor. Pushing a new state after an undo drops the redo branch (standard
 * editor behaviour). Extracted from CrewSchedule so the navigation logic is
 * unit-testable and can't desync — the previous inline version advanced the
 * index on undo but pushed without truncating consistently.
 */

export interface History<T> {
  stack: T[];
  index: number;
}

/** How many versions to keep. Guards against unbounded growth on long sessions. */
const MAX_HISTORY = 100;

export function initHistory<T>(initial: T): History<T> {
  return { stack: [initial], index: 0 };
}

/** Push a new state, discarding any redo branch ahead of the cursor. */
export function pushHistory<T>(history: History<T>, next: T): History<T> {
  const truncated = history.stack.slice(0, history.index + 1);
  truncated.push(next);
  const overflow = Math.max(0, truncated.length - MAX_HISTORY);
  const stack = overflow ? truncated.slice(overflow) : truncated;
  return { stack, index: stack.length - 1 };
}

export function canUndo<T>(history: History<T>): boolean {
  return history.index > 0;
}

export function canRedo<T>(history: History<T>): boolean {
  return history.index < history.stack.length - 1;
}

/** Step back. Returns the same history and current value when already at the start. */
export function undo<T>(history: History<T>): { history: History<T>; value: T } {
  if (!canUndo(history)) return { history, value: history.stack[history.index] };
  const index = history.index - 1;
  return { history: { ...history, index }, value: history.stack[index] };
}

/** Step forward. Returns the same history and current value when already at the end. */
export function redo<T>(history: History<T>): { history: History<T>; value: T } {
  if (!canRedo(history)) return { history, value: history.stack[history.index] };
  const index = history.index + 1;
  return { history: { ...history, index }, value: history.stack[index] };
}
